import { useCallback, useEffect, useRef, useState } from 'react'
import { INSPREKEN_WEB_SPEECH, MAX_RECORDING_MS } from './constants'

/** The parts of the browser's speech recognition this hook uses. */
interface RecognitionResult {
  readonly isFinal: boolean
  readonly 0: { readonly transcript: string }
}
interface RecognitionEvent {
  readonly resultIndex: number
  readonly results: ArrayLike<RecognitionResult>
}
interface Recognition {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: RecognitionEvent) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}
type RecognitionConstructor = new () => Recognition

interface WakeLockSentinelLike {
  release(): Promise<void>
}

export type DictationState = 'klaar' | 'luistert' | 'fout'
export type DictationMode = 'spraak' | 'toetsenbord'

const ACCESS_HELP =
  'Je browser geeft geen toegang tot de microfoon. iPhone: Instellingen › Safari › Microfoon. Android: tik op het slotje › Machtigingen.'

/** B.2.3: what to say for each error of the API, and where to go next. */
const ERRORS: Record<string, { message: string | null; mode: DictationMode }> = {
  'not-allowed': { message: ACCESS_HELP, mode: 'toetsenbord' },
  'service-not-allowed': { message: ACCESS_HELP, mode: 'toetsenbord' },
  'audio-capture': { message: 'Geen microfoon gevonden.', mode: 'toetsenbord' },
  network: {
    message: 'Spraakherkenning heeft internet nodig. Dicteer via je toetsenbord of typ.',
    mode: 'toetsenbord',
  },
  'language-not-supported': { message: 'Nederlands wordt hier niet herkend.', mode: 'toetsenbord' },
  'no-speech': { message: 'Niets gehoord. Tik opnieuw.', mode: 'spraak' },
  aborted: { message: null, mode: 'spraak' },
}
const UNKNOWN_ERROR = { message: 'Spraakherkenning werkt hier niet. Dicteer via je toetsenbord of typ.', mode: 'toetsenbord' as const }

function recognitionConstructor(): RecognitionConstructor | null {
  if (!INSPREKEN_WEB_SPEECH || typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: RecognitionConstructor
    webkitSpeechRecognition?: RecognitionConstructor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Chromium restarts quietly after an unexpected end; Safari does not (it stops without a result). */
function isChromium(): boolean {
  const ua = navigator.userAgent
  return /(Chrome|Chromium|Edg)\//.test(ua) && !/(CriOS|EdgiOS|FxiOS)/.test(ua)
}

export interface SpeechDictation {
  supported: boolean
  mode: DictationMode
  state: DictationState
  /** Words still being recognised, shown grey under the field. */
  interim: string
  elapsedSeconds: number
  /** An explanation to show (error, 5-minute stop), or null. */
  message: string | null
  /** The recording ended by itself: offer "Verder inspreken". */
  canResume: boolean
  start: () => void
  stop: () => void
}

/**
 * Dictation through the browser's speech recognition (ED-67). One instance per page. Final
 * sentences go to `onFinal`; the interim text stays here. Our code never handles audio:
 * the browser asks for the microphone itself.
 */
export function useSpeechDictation({
  lang = 'nl-NL',
  onFinal,
}: {
  lang?: string
  onFinal: (text: string) => void
}): SpeechDictation {
  const [supported] = useState(() => recognitionConstructor() !== null)
  const [mode, setMode] = useState<DictationMode>(supported ? 'spraak' : 'toetsenbord')
  const [state, setState] = useState<DictationState>('klaar')
  const [interim, setInterim] = useState('')
  const [elapsedSeconds, setElapsed] = useState(0)
  const [message, setMessage] = useState<string | null>(null)
  const [canResume, setCanResume] = useState(false)

  const onFinalRef = useRef(onFinal)
  useEffect(() => {
    onFinalRef.current = onFinal
  })

  const recRef = useRef<Recognition | null>(null)
  const stoppingRef = useRef(false)
  const restartedRef = useRef(false)
  const pendingErrorRef = useRef<{ message: string | null; mode: DictationMode } | null>(null)
  const limitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lockRef = useRef<WakeLockSentinelLike | null>(null)
  const wantLockRef = useRef(false)

  const clearTimers = useCallback(() => {
    if (limitTimerRef.current) clearTimeout(limitTimerRef.current)
    if (tickRef.current) clearInterval(tickRef.current)
    limitTimerRef.current = null
    tickRef.current = null
  }, [])

  const releaseLock = useCallback(() => {
    wantLockRef.current = false
    const lock = lockRef.current
    lockRef.current = null
    if (lock) void lock.release().catch(() => null)
  }, [])

  const acquireLock = useCallback(() => {
    const wakeLock = (navigator as unknown as {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> }
    }).wakeLock
    if (!wakeLock) return
    wantLockRef.current = true
    void wakeLock
      .request('screen')
      .then((lock) => {
        if (wantLockRef.current) lockRef.current = lock
        else void lock.release().catch(() => null)
      })
      .catch(() => null)
  }, [])

  const finish = useCallback(() => {
    clearTimers()
    releaseLock()
    recRef.current = null
    setInterim('')
    setState('klaar')
  }, [clearTimers, releaseLock])

  const start = useCallback(() => {
    const Ctor = recognitionConstructor()
    if (!Ctor || mode !== 'spraak' || recRef.current) return
    const rec = new Ctor()
    rec.lang = lang
    rec.interimResults = true
    rec.continuous = true
    stoppingRef.current = false
    restartedRef.current = false
    pendingErrorRef.current = null
    recRef.current = rec

    rec.onresult = (event) => {
      let interimText = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const text = result[0].transcript
        if (result.isFinal) {
          const trimmed = text.trim()
          if (trimmed) onFinalRef.current(trimmed)
        } else {
          interimText += text
        }
      }
      setInterim(interimText)
    }
    rec.onerror = (event) => {
      stoppingRef.current = true
      recRef.current = null
      pendingErrorRef.current = ERRORS[event.error] ?? UNKNOWN_ERROR
      // The UA follows an error with `end`; if it does not, the next stop settles it.
      const outcome = pendingErrorRef.current
      setMessage(outcome.message)
      setMode(outcome.mode)
      setState(outcome.mode === 'toetsenbord' ? 'fout' : 'klaar')
      clearTimers()
      releaseLock()
      setInterim('')
    }
    rec.onend = () => {
      if (pendingErrorRef.current) {
        recRef.current = null
        return
      }
      if (stoppingRef.current) return finish()
      if (isChromium() && !restartedRef.current) {
        restartedRef.current = true
        try {
          rec.start()
          return
        } catch {
          // Could not restart: fall through and offer to continue.
        }
      }
      finish()
      setCanResume(true)
    }

    setMessage(null)
    setCanResume(false)
    setElapsed(0)
    setState('luistert')
    const startedAt = Date.now()
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000))
    }, 1000)
    limitTimerRef.current = setTimeout(() => {
      stoppingRef.current = true
      setMessage('De opname is gestopt na 5 minuten. Tik opnieuw om verder te gaan.')
      rec.stop()
    }, MAX_RECORDING_MS)
    acquireLock()
    rec.start()
  }, [acquireLock, clearTimers, finish, lang, mode, releaseLock])

  const stop = useCallback(() => {
    const rec = recRef.current
    if (!rec) return
    stoppingRef.current = true
    rec.stop()
  }, [])

  useEffect(
    () => () => {
      const rec = recRef.current
      if (rec) {
        rec.onresult = null
        rec.onerror = null
        rec.onend = null
        rec.abort()
        recRef.current = null
      }
      if (limitTimerRef.current) clearTimeout(limitTimerRef.current)
      if (tickRef.current) clearInterval(tickRef.current)
      wantLockRef.current = false
      const lock = lockRef.current
      lockRef.current = null
      if (lock) void lock.release().catch(() => null)
    },
    [],
  )

  return { supported, mode, state, interim, elapsedSeconds, message, canResume, start, stop }
}
